<template>
  <div class="form-group" :class="$attrs.class" role="group">
    <label v-if="label" :for="id" class="form-label">
      {{ label }}
      <span v-if="required" class="required-asterisk">*</span>
      <select
        :id="id"
        ref="selectRef"
        v-model="model"
        class="form-input"
        :disabled="disabled"
        :aria-label="label"
        :aria-required="required"
        :aria-invalid="!!error"
        :aria-describedby="error ? `${id}-error` : undefined"
        :autocomplete="autocomplete"
      >
        <slot></slot>
      </select>
    </label>

    <span v-if="error" :id="`${id}-error`" class="form-error" role="alert">
      {{ error }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { useUniqueId } from '@/composables/useUniqueId'

withDefaults(
  defineProps<{
    label?: string
    disabled?: boolean
    id?: string
    required?: boolean
    error?: string
    autocomplete?: string
  }>(),
  {
    id: () => useUniqueId('select').value,
    label: undefined,
    disabled: false,
    required: false,
    error: undefined,
    autocomplete: undefined,
  }
)

const model = defineModel<string>()
const selectRef = ref<HTMLSelectElement | null>(null)

const focusSelect = () => {
  if (selectRef.value) {
    selectRef.value.focus()
  }
}

defineExpose({
  focusSelect,
})
</script>

<style lang="postcss" scoped>
.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-light text-gray-700;
}

.form-input {
  @apply w-full rounded-lg px-2 pr-8 h-10 mt-2;
  @apply bg-white;
  @apply border-t border-l border-b border-gray-200;
  @apply border-r-[10px] border-r-white;
  @apply outline outline-1 outline-gray-200;
  @apply focus:outline-primary hover:outline-gray-400;
  @apply transition-all duration-200 ease-in-out;

  &:focus {
    box-shadow:
      0px 1px 1px rgba(0, 0, 0, 0.03),
      0px 3px 6px rgba(0, 0, 0, 0.02);
  }
}

.form-error {
  @apply text-sm text-red;
}

.required-asterisk {
  @apply text-red ml-0.5;
}
</style>
